import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/app/components/ui/dialog'
import { Button } from '@/app/components/ui/button'
import { Input } from '@/app/components/ui/input'
import type { CashCapacity } from '@/app/services/pos.service'

export interface CashDepositModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  currentCashInRegister: number
  depositAmount: string
  setDepositAmount: (amount: string) => void
  depositConcept: string
  setDepositConcept: (concept: string) => void
  isSubmitting?: boolean
  handleCashDeposit: () => void
  setShowCashDeposit: (show: boolean) => void
  cashCapacity: CashCapacity | null
}

export function CashDepositModal({
  open,
  onOpenChange,
  currentCashInRegister,
  depositAmount,
  setDepositAmount,
  depositConcept,
  setDepositConcept,
  isSubmitting,
  handleCashDeposit,
  setShowCashDeposit,
  cashCapacity,
}: CashDepositModalProps) {
  const remainingCapacity = Number(cashCapacity?.remaining_amount || 0)
  const parsedDeposit = Number(depositAmount || 0)
  const exceedsCashLimit = Boolean(cashCapacity) && parsedDeposit > remainingCapacity

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Ingreso de Efectivo</DialogTitle>
          <DialogDescription className="sr-only">
            Ingresar efectivo a la caja
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="rounded-lg bg-slate-50 p-4">
            <p className="text-sm text-slate-600">Efectivo actual en caja:</p>
            <p className="text-2xl font-semibold text-slate-900">
              ${currentCashInRegister.toFixed(2)} MXN
            </p>
          </div>

          {cashCapacity && (
            <div className={`rounded-lg border p-3 ${exceedsCashLimit ? 'border-red-200 bg-red-50' : 'border-slate-200 bg-white'}`}>
              <p className={`text-sm ${exceedsCashLimit ? 'text-red-700' : 'text-slate-600'}`}>
                Capacidad disponible: ${remainingCapacity.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN
              </p>
            </div>
          )}

          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">
              Monto a ingresar: <span className="text-red-600">*</span>
            </label>
            <Input
              type="number"
              value={depositAmount}
              onChange={(e) => setDepositAmount(e.target.value)}
              placeholder="0.00"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">
              Concepto: <span className="text-red-600">*</span>
            </label>
            <Input
              value={depositConcept}
              onChange={(e) => setDepositConcept(e.target.value)}
              placeholder="Ej: Fondo de caja, reposición..."
            />
          </div>

          <div className="flex gap-2">
            <Button
              onClick={handleCashDeposit}
              className="flex-1"
              disabled={
                !depositAmount || parseFloat(depositAmount) <= 0 || exceedsCashLimit || !depositConcept.trim() || isSubmitting
              }
            >
              {isSubmitting ? 'Procesando...' : 'Confirmar Ingreso'}
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setShowCashDeposit(false)
                setDepositAmount('')
                setDepositConcept('')
              }}
            >
              Cancelar
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
